import { useEffect, useState } from "react";
import axios from "axios";
import {
  Smartphone, RefreshCw, Apple as AppleIcon, Wallet, MapPin, ArrowRight,
} from "lucide-react";
import { API } from "../../config";
import { C360_FILTER_COUNTRY_EVENT } from "./Customer360Widget";
import { useCountryFilter } from "../../contexts/CountryFilterContext";


const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", {
      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

const WALLET_STYLE = {
  apple: { label: "Apple Wallet", color: "text-gray-200", bg: "bg-gray-500/15", border: "border-gray-500/30" },
  google: { label: "Google Wallet", color: "text-blue-300", bg: "bg-blue-500/15", border: "border-blue-500/30" },
};


export const WalletAdoptionWidget = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { country, isFiltered } = useCountryFilter();

  const fetchData = async () => {
    try {
      setLoading(true);
      // Backend already supports `?country=XX` natively (existing
      // signature). Skip the param when "Tutti i paesi" is selected.
      const params = isFiltered ? { country } : {};
      const r = await axios.get(`${API}/dashboard/wallet-adoption`, { params });
      setData(r.data);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("wallet-adoption fetch failed", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const t = setInterval(fetchData, 120_000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country]);

  if (!data) {
    return (
      <div className="bg-[#111] border border-gray-800 rounded-2xl p-6" data-testid="wallet-adoption-empty">
        {loading
          ? <div className="text-gray-500 text-sm flex items-center gap-2"><RefreshCw size={14} className="animate-spin" /> Caricamento…</div>
          : <p className="text-gray-500 text-sm">Nessun dato wallet</p>}
      </div>
    );
  }

  const hasSigned = data.signed_customers > 0;

  return (
    <div
      className="bg-gradient-to-br from-[#0a141a] via-[#0a0a0a] to-[#0a141a] border border-blue-500/15 rounded-2xl p-6 space-y-5"
      data-testid="wallet-adoption-widget"
    >
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center">
            <Wallet size={20} className="text-blue-400" />
          </div>
          <div>
            <h3 className="text-white font-bold text-lg">Wallet Adoption</h3>
            <p className="text-gray-500 text-xs">
              {hasSigned
                ? <>Su <span className="text-white">{data.signed_customers}</span> clienti firmati · <span className="text-lojack-blue-400">{data.either_adoption_pct.toFixed(1)}%</span> ha aggiunto la card al wallet</>
                : <>Nessun contratto firmato ancora · totali all-time mostrati sotto</>}
            </p>
          </div>
        </div>
        <button
          onClick={fetchData}
          disabled={loading}
          className="p-2 text-gray-500 hover:text-blue-400 transition-colors"
          title="Aggiorna"
          data-testid="wallet-adoption-refresh"
        >
          <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      {/* KPI cards: Apple + Google side by side */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

        {/* Apple Wallet */}
        <div className="bg-black/40 border border-gray-700 rounded-xl p-4" data-testid="wallet-kpi-apple">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <AppleIcon size={16} className="text-gray-200" />
              <p className="text-white text-sm font-semibold">Apple Wallet</p>
            </div>
            <span className="text-gray-500 text-[10px] uppercase tracking-wider">all-time: {data.apple_added_all_time}</span>
          </div>
          <div className="flex items-baseline gap-2">
            <p className="text-3xl font-bold text-white">{data.apple_added}</p>
            <p className="text-gray-500 text-sm">
              {hasSigned && <>/ {data.signed_customers} firmati</>}
            </p>
          </div>
          {hasSigned ? (
            <>
              <div className="mt-3 h-2 bg-gray-900 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-gray-400 to-gray-200 transition-all duration-500 rounded-full"
                  style={{ width: `${Math.max(2, data.apple_adoption_pct)}%` }}
                />
              </div>
              <p className="text-gray-400 text-xs mt-2">
                <span className="text-white font-semibold">{data.apple_adoption_pct.toFixed(1)}%</span> adoption rate
              </p>
            </>
          ) : (
            <p className="text-gray-500 text-xs mt-3">Adoption rate disponibile dopo le prime firme</p>
          )}
        </div>

        {/* Google Wallet */}
        <div className="bg-black/40 border border-blue-500/20 rounded-xl p-4" data-testid="wallet-kpi-google">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Smartphone size={16} className="text-blue-300" />
              <p className="text-white text-sm font-semibold">Google Wallet</p>
            </div>
            <span className="text-gray-500 text-[10px] uppercase tracking-wider">all-time: {data.google_added_all_time}</span>
          </div>
          <div className="flex items-baseline gap-2">
            <p className="text-3xl font-bold text-white">{data.google_added}</p>
            <p className="text-gray-500 text-sm">
              {hasSigned && <>/ {data.signed_customers} firmati</>}
            </p>
          </div>
          {hasSigned ? (
            <>
              <div className="mt-3 h-2 bg-gray-900 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-blue-400 to-blue-300 transition-all duration-500 rounded-full"
                  style={{ width: `${Math.max(2, data.google_adoption_pct)}%` }}
                />
              </div>
              <p className="text-gray-400 text-xs mt-2">
                <span className="text-white font-semibold">{data.google_adoption_pct.toFixed(1)}%</span> adoption rate
              </p>
            </>
          ) : (
            <p className="text-gray-500 text-xs mt-3">Adoption rate disponibile dopo le prime firme</p>
          )}
        </div>
      </div>

      {/* Cross-adoption strip */}
      {hasSigned && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="wallet-adoption-strip">
          <div className="bg-black/40 border border-gray-800 rounded-lg p-3">
            <p className="text-gray-500 text-[10px] uppercase tracking-wider mb-1">Almeno uno</p>
            <p className="text-white text-lg font-bold">
              {data.either_added}
              <span className="text-gray-500 text-xs font-normal ml-1">({data.either_adoption_pct.toFixed(1)}%)</span>
            </p>
          </div>
          <div className="bg-black/40 border border-gray-800 rounded-lg p-3">
            <p className="text-gray-500 text-[10px] uppercase tracking-wider mb-1">Entrambi</p>
            <p className="text-white text-lg font-bold">
              {data.both_added}
              <span className="text-gray-500 text-xs font-normal ml-1">({data.both_adoption_pct.toFixed(1)}%)</span>
            </p>
          </div>
          <div className="bg-black/40 border border-gray-800 rounded-lg p-3">
            <p className="text-gray-500 text-[10px] uppercase tracking-wider mb-1">Solo Apple</p>
            <p className="text-white text-lg font-bold">
              {Math.max(0, data.apple_added - data.both_added)}
            </p>
          </div>
          <div className="bg-black/40 border border-gray-800 rounded-lg p-3">
            <p className="text-gray-500 text-[10px] uppercase tracking-wider mb-1">Solo Google</p>
            <p className="text-white text-lg font-bold">
              {Math.max(0, data.google_added - data.both_added)}
            </p>
          </div>
        </div>
      )}

      {/* Per-country adoption breakdown */}
      {data.country_breakdown && data.country_breakdown.length > 0 && hasSigned && (
        <div className="bg-black/40 border border-gray-800 rounded-xl p-4" data-testid="wallet-country-breakdown">
          <div className="flex items-center gap-2 mb-3">
            <MapPin size={14} className="text-blue-400" />
            <p className="text-white text-sm font-semibold">Adoption per Paese</p>
            <span className="text-gray-500 text-[10px] uppercase tracking-wider">{data.country_breakdown.length} {data.country_breakdown.length === 1 ? "paese" : "paesi"}</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-gray-800 text-gray-500 text-[10px] uppercase tracking-wider">
                  <th className="py-2 text-left font-medium">Paese</th>
                  <th className="py-2 text-center font-medium">Firmati</th>
                  <th className="py-2 text-center font-medium">
                    <span className="inline-flex items-center gap-1">
                      <AppleIcon size={10} /> Apple
                    </span>
                  </th>
                  <th className="py-2 text-center font-medium">
                    <span className="inline-flex items-center gap-1">
                      <Smartphone size={10} /> Google
                    </span>
                  </th>
                  <th className="py-2 text-left font-medium pl-4">Adoption (≥1 wallet)</th>
                </tr>
              </thead>
              <tbody>
                {data.country_breakdown.map((c) => {
                  const adoptionWidth = Math.max(2, c.either_pct);
                  const adoptionColor =
                    c.either_pct >= 60 ? "bg-lojack-blue-500" :
                    c.either_pct >= 30 ? "bg-lojack-clause-500" :
                    c.either_pct > 0 ? "bg-orange-500" :
                    "bg-red-500/60";
                  // Click handler: dispatch the cross-widget filter event
                  // so the Customer 360 widget pre-filters by this country
                  // and scrolls itself into view.
                  const drill = () => {
                    window.dispatchEvent(new CustomEvent(C360_FILTER_COUNTRY_EVENT, {
                      detail: { country: c.country },
                    }));
                  };
                  return (
                    <tr
                      key={c.country}
                      onClick={drill}
                      className="border-b border-gray-800/50 hover:bg-purple-500/5 cursor-pointer transition-colors group"
                      title={`Apri Customer 360° filtrato per ${c.country}`}
                      data-testid={`wallet-country-${c.country}`}
                    >
                      <td className="py-2 text-white font-mono font-semibold">
                        <span className="inline-flex items-center gap-1.5">
                          {c.country}
                          <ArrowRight size={10} className="text-gray-600 group-hover:text-purple-400 transition-colors" />
                        </span>
                      </td>
                      <td className="py-2 text-center text-gray-300">{c.signed}</td>
                      <td className="py-2 text-center">
                        <span className="text-white">{c.apple}</span>
                        <span className="text-gray-600 text-[10px] ml-1">({c.apple_pct.toFixed(0)}%)</span>
                      </td>
                      <td className="py-2 text-center">
                        <span className="text-white">{c.google}</span>
                        <span className="text-gray-600 text-[10px] ml-1">({c.google_pct.toFixed(0)}%)</span>
                      </td>
                      <td className="py-2 pl-4">
                        <div className="flex items-center gap-2 min-w-[160px]">
                          <div className="flex-1 h-1.5 bg-gray-900 rounded-full overflow-hidden">
                            <div className={`h-full ${adoptionColor} transition-all duration-500 rounded-full`} style={{ width: `${adoptionWidth}%` }} />
                          </div>
                          <span className="text-gray-300 text-[11px] whitespace-nowrap w-12 text-right">
                            {c.either_pct.toFixed(1)}%
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="text-gray-600 text-[10px] mt-3">
            Adoption rate &lt; 30% (rosso/arancio) può indicare problemi tecnici regionali o serve marketing localizzato. Paesi con &lt;5 firmati hanno KPI statisticamente poco significativi. <span className="text-purple-400">Clicca su un paese</span> per aprire il Customer 360° filtrato.
          </p>
        </div>
      )}

      {/* Recent activity */}
      {data.recent_additions.length > 0 && (
        <div className="bg-black/40 border border-gray-800 rounded-xl p-4" data-testid="wallet-recent-activity">
          <p className="text-gray-400 text-[10px] uppercase tracking-wider mb-3">Ultime aggiunte al wallet</p>
          <div className="space-y-1.5">
            {data.recent_additions.map((e, i) => {
              const style = WALLET_STYLE[e.wallet] || WALLET_STYLE.google;
              return (
                <div key={i} className="flex items-center gap-3 text-xs" data-testid={`wallet-activity-${i}`}>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${style.bg} ${style.color} border ${style.border} whitespace-nowrap`}>
                    {style.label}
                  </span>
                  <span className="text-white flex-1 truncate" title={e.name}>{e.name}</span>
                  <span className="text-gray-500 whitespace-nowrap">{e.country || "—"}</span>
                  <span className="text-gray-500 font-mono whitespace-nowrap">{e.plate || "—"}</span>
                  <span className="text-gray-500 whitespace-nowrap">{fmtDateTime(e.at)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <p className="text-gray-600 text-[10px] text-center">
        Ultimo aggiornamento: {new Date(data.generated_at).toLocaleString("it-IT")} · Auto-refresh ogni 120s
      </p>
    </div>
  );
};

export default WalletAdoptionWidget;
